/**
 * The pharmacy notifications button and panel (design/ux/01_foundations.md
 * §6.2, §6.14; AUD-NAV-16, AUD-NAV-22; WP-18).
 *
 *  · The button names the unread count (« Notifications, 3 non lues »).
 *  · The panel is a dialog-like popover (non-modal): focus moves to its first
 *    row; rows are real buttons reachable with Tab AND ↑/↓ (Début / Fin);
 *    Entrée opens the row's page; « Tout marquer comme lu »; Échap closes and
 *    returns focus to the button; a click outside closes it. Row text ≥ 14 px,
 *    the tone in words (« Attention ») as well as icon and colour.
 *  · Deep link: any page opened with `?panel=notifications` opens the panel
 *    (the retargeted server links of §6.14 use `/dashboard?panel=notifications`).
 */
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button, Icon } from "@/components/ui/primitives";
import { fmtRelTime } from "@/config/locale.config.js";
import { pageToPath } from "@/config/navigation.config.js";
import { SHELL_PANEL_PARAM, SHELL_PANELS } from "@/config/ui.config.js";
import { useNotificationStore } from "@/stores/index.js";
import { moveIndex } from "@/lib/nav-tree.js";
import {
  NOTIFICATION_TONE_ICON,
  NOTIFICATION_TONE_LABEL,
  isNotificationRead,
  notificationTarget,
  notificationTone,
} from "@/lib/shell-notifications.js";

export const NotificationsPopover: React.FC = () => {
  const notifications: any[] = useNotificationStore((s: any) => s.notifications) ?? [];
  const unreadCount: number = useNotificationStore((s: any) => s.unread) ?? 0;
  const fetchNotifications = useNotificationStore((s: any) => s.fetch);
  const markRead = useNotificationStore((s: any) => s.markRead);
  const markAllRead = useNotificationStore((s: any) => s.markAllRead);
  const isLoading = useNotificationStore((s: any) => s.loading);
  const errorMsg = useNotificationStore((s: any) => s.error);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = React.useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const titleId = React.useId();
  const panelId = React.useId();

  // Refetch the list whenever the panel opens (the badge only polls a count).
  React.useEffect(() => {
    if (open) fetchNotifications?.({ page: 1, unread_only: false });
  }, [open, fetchNotifications]);

  // `?panel=notifications` opens the panel, then leaves the URL clean.
  const requested = params.get(SHELL_PANEL_PARAM) === SHELL_PANELS.NOTIFICATIONS;
  React.useEffect(() => {
    if (!requested) return;
    setOpen(true);
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete(SHELL_PANEL_PARAM);
        return p;
      },
      { replace: true },
    );
  }, [requested, setParams]);

  const close = React.useCallback((restore: boolean) => {
    setOpen(false);
    if (restore) buttonRef.current?.focus();
  }, []);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || buttonRef.current?.contains(t)) return;
      close(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [open, close]);

  // Focus the first row (else « Tout marquer comme lu », else the panel).
  const focused = React.useRef(false);
  React.useEffect(() => {
    if (!open) {
      focused.current = false;
      return;
    }
    if (focused.current) return;
    const panel = panelRef.current;
    if (!panel) return;
    // Rows that arrive after opening take focus only while the user has not
    // moved it elsewhere (async results never steal focus, §4.5).
    const active = document.activeElement;
    if (active && active !== document.body && active !== panel && !buttonRef.current?.contains(active)) {
      focused.current = true;
      return;
    }
    const row = panel.querySelector<HTMLElement>("[data-notif-row]");
    if (row) {
      row.focus();
      focused.current = true;
    } else panel.focus();
  }, [open, isLoading, notifications.length]);

  const rows = () => Array.from(panelRef.current?.querySelectorAll<HTMLElement>("[data-notif-row]") ?? []);
  const onPanelKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close(true);
      return;
    }
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
    const list = rows();
    if (list.length === 0) return;
    e.preventDefault();
    const i = list.indexOf(document.activeElement as HTMLElement);
    list[moveIndex(list.length, i < 0 ? -1 : i, e.key)]?.focus();
  };
  const onPanelBlur = (e: React.FocusEvent) => {
    const next = e.relatedTarget as Node | null;
    if (next && !panelRef.current?.contains(next) && !buttonRef.current?.contains(next)) close(false);
  };

  const unread = notifications.filter((n) => !isNotificationRead(n)).length;
  const name =
    unreadCount > 0
      ? `Notifications, ${unreadCount} non lue${unreadCount > 1 ? "s" : ""}`
      : "Notifications";

  return (
    <div className="pk-topbar-notif">
      <button
        ref={buttonRef}
        type="button"
        className="pk-topbar-iconbtn"
        aria-label={name}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => (open ? close(true) : setOpen(true))}
      >
        <Icon name="bell" size={20} />
        {unreadCount > 0 && (
          <span aria-hidden="true" className="pk-topbar-count">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
          tabIndex={-1}
          className="pk-notif-panel"
          onKeyDown={onPanelKeyDown}
          onBlur={onPanelBlur}
        >
          <div className="pk-notif-head">
            <div>
              <h2 id={titleId} className="pk-notif-title">
                Notifications
              </h2>
              <p className="pk-notif-sub">
                {unread === 0 ? "Aucune non lue" : `${unread} non lue${unread > 1 ? "s" : ""}`}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              data-notif-markall=""
              disabled={unread === 0}
              onClick={() => markAllRead?.()}
            >
              Tout marquer comme lu
            </Button>
          </div>
          <ul className="pk-notif-list">
            {isLoading && notifications.length === 0 && <li className="pk-notif-state">Chargement…</li>}
            {errorMsg && !isLoading && (
              <li className="pk-notif-state pk-notif-state--error">
                Les notifications n'ont pas pu être chargées : {errorMsg}
              </li>
            )}
            {!isLoading && !errorMsg && notifications.length === 0 && (
              <li className="pk-notif-state">Aucune notification.</li>
            )}
            {notifications.map((n) => {
              const tone = notificationTone(n);
              const read = isNotificationRead(n);
              const target = notificationTarget(n, pageToPath);
              return (
                <li key={n.id}>
                  <button
                    type="button"
                    data-notif-row=""
                    className={cn("pk-notif-row", `pk-notif-row--${tone}`, !read && "is-unread")}
                    onClick={() => {
                      if (!read) markRead?.(n.id);
                      close(false);
                      if (target) navigate(target);
                    }}
                  >
                    <Icon name={NOTIFICATION_TONE_ICON[tone as keyof typeof NOTIFICATION_TONE_ICON]} size={18} className="pk-notif-icon" />
                    <span className="pk-notif-body">
                      <span className="pk-notif-row-title">
                        <span className="sr-only">{`${NOTIFICATION_TONE_LABEL[tone as keyof typeof NOTIFICATION_TONE_LABEL]} : `}</span>
                        {n.titre}
                        {!read && (
                          <span className="pk-notif-unread">
                            <span className="sr-only"> (non lue)</span>
                          </span>
                        )}
                      </span>
                      {n.message && <span className="pk-notif-msg">{n.message}</span>}
                      <span className="pk-notif-time">{fmtRelTime(n.created_at)}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default NotificationsPopover;
